import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress } from '@material-ui/core';
import { KeyboardBackspace } from '@material-ui/icons';
import type { ProjectSubmissionFormBody } from '@mogul/api-client';
import { convertFileToBase64 } from '@mogul/shared';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FormProvider, useForm } from 'react-hook-form';

import { projectsApi } from '../../api';
import { captchaServerKey } from '../../config';
import {
  loadFormData,
  removeFormData,
  saveFormData,
} from '../../lib/localForage/localForage';
import type { ProjectSubmissionFormInterface } from './form/schema';
import { fileFields, schema } from './form/schema';
import {
  BusinessDetailsStep,
  ContinuedBusinessDetailsStep,
  DetailsStep,
  IntroStep,
  PersonStep,
  PitchStep,
  ProjectStep,
  UploadAssetsStep,
} from './form/steps';
import styles from './ProjectSubmissionForm.module.css';

const LOCAL_FORAGE_SUBMISSION_FORM_KEY = 'submissionForm';

interface StepWrapperProps {
  goNextStep?: () => void;
  goPrevStep?: () => void;
  title: string;
  image?: string;
}

const StepWrapperPrevButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = props => (
  <button type="button" className={styles.prevButton} {...props}>
    <KeyboardBackspace />
    <p>Back</p>
  </button>
);

const StepWrapperNextButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { text?: string }
> = ({ text, ...props }) => (
  <button type="button" className={styles.nextButton} {...props}>
    <p>{text ?? 'Next'}</p>
  </button>
);

const StepWrapperIntroButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = props => (
  <StepWrapperNextButton
    text="LET’S GET STARTED"
    style={{ width: 228 }}
    {...props}
  />
);

const StepWrapperSubmitButton = () => (
  <button type="submit" className={styles.nextButton}>
    <p>Submit</p>
  </button>
);

const StepWrapper: React.FC<StepWrapperProps> = ({
  children,
  goPrevStep,
  goNextStep,
  title,
  image,
}) => (
  <div className="flex lg:flex-row flex-col">
    {image != null && (
      <img
        src={image}
        alt="Step"
        className={styles.submissionFormImg + ' w-full h-auto object-cover'}
      />
    )}
    <div className="flex flex-col gap-6 p-8 w-full box-border">
      <h1 className={styles.stepHeader}>{title}</h1>
      {children}
      {goPrevStep ? (
        <div className="flex justify-end">
          <StepWrapperPrevButton onClick={goPrevStep} />
          {goNextStep ? (
            <StepWrapperNextButton onClick={goNextStep} />
          ) : (
            <StepWrapperSubmitButton />
          )}
        </div>
      ) : (
        <StepWrapperIntroButton onClick={goNextStep} />
      )}
    </div>
  </div>
);

const steps = [
  {
    component: <IntroStep />,
    wrapperProps: {
      title: 'SUBMIT PROJECT',
      image: '/images/cinema.jpg',
    },
  },
  {
    component: <PersonStep />,
    wrapperProps: {
      title: 'LET’S GET TO KNOW YOU!',
      image: '/images/director.jpg',
    },
    fieldsToValidate: ['name', 'email'] as const,
  },
  {
    component: <ProjectStep />,
    wrapperProps: {
      title: 'TELL US ABOUT YOUR PROJECT',
      image: '/images/shooting.jpg',
    },
    fieldsToValidate: ['projectName', 'genre', 'screenwriter'] as const,
  },
  {
    component: <PitchStep />,
    wrapperProps: {
      title: 'TIME TO PITCH!',
      image: '/images/laptop.jpg',
    },
  },
  {
    component: <DetailsStep />,
    wrapperProps: {
      title: 'MORE DETAILS!',
      image: '/images/cars.jpg',
    },
  },
  {
    component: <BusinessDetailsStep />,
    wrapperProps: {
      title: 'BUSINESS DETAILS',
    },
    fieldsToValidate: [
      'status',
      'budgetPercent',
      'estimatedBudget',
      'mogulBudget',
    ] as const,
  },
  {
    component: <ContinuedBusinessDetailsStep />,
    wrapperProps: {
      title: 'BUSINESS DETAILS (CONTINUED)',
    },
    fieldsToValidate: ['utilities'] as const,
  },
  {
    component: <UploadAssetsStep />,
    wrapperProps: {
      title: 'UPLOAD ASSETS',
      image: '/images/laptop.jpg',
    },
  },
];

const Conclusion = ({ title, text }: { title: string; text: string }) => {
  return (
    <div className={`${styles.submission} flex flex-col p-6`}>
      <h1 className={styles.stepHeader}>{title}</h1>
      <p>{text}</p>
    </div>
  );
};

const SuccessfulConclusion = () => (
  <Conclusion
    title="THANK YOU FOR SUBMITTING!"
    text="Your project has been submitted successfully. We will get back to you soon."
  />
);

const ErrorConclusion = () => (
  <Conclusion title="SOMETHING WENT WRONG" text="Please, try again later" />
);

enum ProjectSubmissionFormStatus {
  LOADING = 'loading',
  SUBMITTED = 'submitted',
  ERROR = 'error',
}

export const ProjectSubmissionForm = () => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const getFormDefaultValues = async () => {
    const savedData = await loadFormData<ProjectSubmissionFormInterface>(
      LOCAL_FORAGE_SUBMISSION_FORM_KEY,
    );
    if (savedData != null) {
      return savedData;
    }

    return {
      original: 'Adaptation',
      team: [{ name: '', image: undefined }],
    } as ProjectSubmissionFormInterface;
  };

  const methods = useForm<ProjectSubmissionFormInterface>({
    resolver: yupResolver(schema),
    defaultValues: getFormDefaultValues,
  });

  const [step, setStep] = useState<number>(0);
  const [status, setStatus] =
    useState<null | ProjectSubmissionFormStatus>(null);

  const formValues = methods.watch();

  useEffect(() => {
    const saveData = _.debounce(async () => {
      const dataToSave = { ...formValues };
      await saveFormData(LOCAL_FORAGE_SUBMISSION_FORM_KEY, dataToSave);
    }, 1000);

    if (status == null) {
      void saveData();
    }
  }, [formValues]);

  const goNextStep = async () => {
    const isValid = await methods.trigger(steps[step]?.fieldsToValidate ?? []);
    if (isValid) {
      setStep(prev => prev + 1);
    }
  };

  const goPrevStep = () => setStep(prev => prev - 1);

  const submitHandler = async (data: ProjectSubmissionFormInterface) => {
    setStatus(ProjectSubmissionFormStatus.LOADING);

    const token = await recaptchaRef.current!.executeAsync();
    const formData = {
      token,
      ...data,
    };

    for (const field of fileFields) {
      const file = formData[field];
      if (file != null) {
        try {
          formData[field] = await convertFileToBase64(file as File);
        } catch (err) {
          console.error(`Convert ${field} to base64 has failed`, err);
          formData[field] = undefined;
        }
      } else {
        formData[field] = undefined;
      }
    }

    const formTeamMembers = await Promise.all(
      formData.team.map(async teamMember => {
        const formTeamMember = { ...teamMember };
        const file = formTeamMember.image;
        if (file != null) {
          try {
            formTeamMember.image = await convertFileToBase64(file as File);
          } catch (err) {
            console.error(`Convert team member to base64 has failed`, err);
            formTeamMember.image = undefined;
          }
        } else {
          formTeamMember.image = undefined;
        }
        return formTeamMember;
      }),
    );

    try {
      await projectsApi.createSubmissionFormRecord({
        projectSubmissionFormBody: {
          ...formData,
          team: formTeamMembers,
        } as ProjectSubmissionFormBody,
      });

      methods.reset();
      await removeFormData(LOCAL_FORAGE_SUBMISSION_FORM_KEY);
      setStatus(ProjectSubmissionFormStatus.SUBMITTED);
    } catch (err) {
      setStatus(ProjectSubmissionFormStatus.ERROR);
      console.error(err);
    }
  };

  return (
    <FormProvider {...methods}>
      {status == null && (
        <div className={`${styles.submission} ${styles.submissionForm}`}>
          <form
            className="flex flex-col"
            onSubmit={methods.handleSubmit(submitHandler)}
          >
            <StepWrapper
              goPrevStep={step !== 0 ? goPrevStep : undefined}
              goNextStep={step !== steps.length - 1 ? goNextStep : undefined}
              {...steps[step]?.wrapperProps}
            >
              {steps[step]?.component}
            </StepWrapper>
          </form>
        </div>
      )}
      {status === ProjectSubmissionFormStatus.LOADING && <CircularProgress />}
      {status === ProjectSubmissionFormStatus.SUBMITTED && (
        <SuccessfulConclusion />
      )}
      {status === ProjectSubmissionFormStatus.ERROR && <ErrorConclusion />}
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={captchaServerKey}
        size="invisible"
      />
    </FormProvider>
  );
};
